import React, { useState, useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';

import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { List, Typography, Dropdown, Button, Col, Form, Spin, App, Badge, Popover, Space } from 'antd';
import { MenuProps } from 'antd';

import { DownOutlined, ClockCircleOutlined, ExclamationCircleOutlined, BellOutlined, RollbackOutlined } from '@ant-design/icons';

import ReminderComponent from '../../reminder';
import ReAssignModal from '../../../re-assign-modal';
import AssignNoteToTaskModal from '../../../note-assign-task-modal/index';

import { getTinyMCEEditorTheme } from '../../../../../shared/utils';
import { siteUrls } from '../../../../../shared/constants/siteUrls';
import { IFrame } from '../../../../../shared/components/iframe';

import * as actions from '../../../store/actions';

import { Note, NotePolicy, NotesRequestBase, ReplyNoteRequest } from '../../../../../shared/models';
import { NoteOwnerTypes } from '../../../../../shared/constants/enums';

import { RootState } from '../../../../../store';

import '../../../styles.less';

const { Text } = Typography;

export type CommentProps = {
  item: Note;
  showArchiveEnabled?: boolean;
  compactModeEnabled?: boolean;
  cancelAddComment: () => void;
  refresh: (res?: any) => void;
  parentNoteId: string | null | undefined;
  baseRequestParams: NotesRequestBase;
  ownerType: NoteOwnerTypes | undefined;
  insuredId: string | null | undefined;
  isSuperUserNotes?: boolean;
  controlConfig: {
    isMomentumEnabled: boolean;
  };
  searchString?: string | null | undefined;
  notePolicies?: NotePolicy[];
};

type MenuItem = Required<MenuProps>['items'][number];

const Comment: React.FC<CommentProps> = ({
  item,
  cancelAddComment,
  refresh,
  parentNoteId,
  baseRequestParams,
  ownerType,
  insuredId,
  isSuperUserNotes,
  controlConfig,
  searchString,
  notePolicies,
}) => {
  const mceEditorId = `commentSubjectEditor${parentNoteId}`;

  const { modal } = App.useApp();

  const [remindersInfo, setRemindersInfo] = useState<string[]>([]);
  const [reassignModalVisible, setReassignModalVisibility] = useState<boolean>(false);
  const [isReminderApplied, setReminder] = useState<boolean>(false);
  const [editorLoading, setEditorLoading] = useState<boolean>(true);
  const [isSubjectValid, setSubjectValidity] = useState<boolean>(true);
  const [subject, setSubject] = useState<string>('');
  const [assignNoteToTaskModalVisible, setAssignNoteToTaskModalVisible] = useState<boolean>(false);
  const [searchSubject, setSearchSubject] = useState<string | null>(null);

  const createNoteCommentLoading = useSelector((state: RootState) => state.notes.createNoteCommentLoading);
  const archiveNoteLoading = useSelector((state: RootState) => state.notes.archiveNoteLoading);

  const dispatch = useDispatch();
  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    return () => {
      try {
        // @ts-ignore - tinymce might not be defined in the TypeScript context
        tinymce.remove(mceEditorId);
      } catch (e) {
        console.log(e);
      }
    };
  }, []);

  useEffect(() => {
    if (!!item.reminderSentToCustomEMail) {
      setRemindersInfo(item.reminderSentToCustomEMail.split(','));
    }
  }, [item]);

  useEffect(() => {
    if (!!searchString) {
      setSearchSubject(searchString);
    }
  }, [searchString]);

  const [form] = Form.useForm();
  const [reminderForm] = Form.useForm();

  const validateMessages = {
    required: '${label} is required.',
    types: {
      email: '${label} is not valid email.',
      number: '${label} is not a valid number.',
    },
  };

  const showAssignTaskModal = (): void => {
    setAssignNoteToTaskModalVisible(true);
  };

  const assignNoteToTaskModalClose = (): void => {
    setAssignNoteToTaskModalVisible(false);
    refresh();
  };

  const handleSave = (): void => {
    setSubjectValidity(subject !== '');
    if (subject !== '') {
      let formData: ReplyNoteRequest | any = {};

      if (isReminderApplied) {
        reminderForm
          .validateFields()
          .then((values) => {
            const formValues = setValues();
            formData = { ...values, ...formValues, ...baseRequestParams };
            save(formData);
          })
          .catch(() => {
            return;
          });
      } else {
        formData = setValues();
        save(formData);
      }
    }
  };

  const setValues = (): ReplyNoteRequest => {
    const formData: ReplyNoteRequest = {
      subject,
      parentNoteId,
      ownerType
    } as ReplyNoteRequest;
    return formData;
  };

  const save = (formData: ReplyNoteRequest): void => {
    formData.subject = `<div>${subject}</div>`;

    dispatch(actions.createNoteComment(formData) as any).then((res: any) => {
      setSubject('');
      // @ts-ignore - tinymce might not be defined in the TypeScript context
      tinymce.get(mceEditorId).resetContent();
      form.resetFields();
      reminderForm.resetFields();

      refresh(res);
    });
  };

  const handleReminderChange = (checked: boolean): void => {
    setReminder(checked);
  };

  const handleSubjectChange = (e: any): void => {
    setSubject(e.target.getContent());
    setSubjectValidity(e.target.getContent() !== '');
  };

  const reassignModalClose = (shouldRefresh?: boolean): void => {
    setReassignModalVisibility(false);

    if (shouldRefresh) {
      refresh();
    }
  };

  const showDeleteConfirm = (id: string): void => {
    modal.confirm({
      title: 'Are you sure archive this comment?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      okButtonProps: { disabled: archiveNoteLoading },
      onOk() {
        return handleArchiveComment(id, true);
      },
    });
  };

  const handleArchiveComment = (id: string, archive: boolean) => {
    return dispatch(actions.archiveNote(id, archive) as any).then((res: any) => {
      refresh(res);
    });
  };

  const menu: MenuItem[] = [
    ...(!isSuperUserNotes
      ? [
        {
          key: '1e',
          label: (
            <a
              href={siteUrls.getTaskCreateUrl(
                controlConfig.isMomentumEnabled,
                item.id,
                insuredId,
                notePolicies ? notePolicies.map((x) => x.policyId) : [],
                false,
                null
              )}
            >
              Create Task
            </a>
          ),
        },
      ]
      : []),
    ...(!isSuperUserNotes
      ? [
        {
          key: '2e',
          label: <a href={siteUrls.getServiceRequestCreateUrl(controlConfig.isMomentumEnabled, item.id, insuredId, null)}>Create Service Request</a>,
        },
      ]
      : []),
    ...(!isSuperUserNotes
      ? [
        {
          key: '3e',
          label: <a href={siteUrls.getCreateToDoUrl(controlConfig.isMomentumEnabled, item.id, insuredId)}>Create To-Do</a>,
        },
      ]
      : []),
    {
      key: '4e',
      onClick: () => showDeleteConfirm(item.id),
      label: 'Archive',
    },
    // ... !isSuperUserNotes ? [{
    //   label: 'Assign to Task',
    //   onClick: () => showAssignTaskModal(),
    //   key: '5e'
    // }] : [],
  ];

  if (item) {
    if (item.state === 'new') {
      return (
        <List.Item className="comment-form-list-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '60%' }}>
            <Form form={form} layout="vertical" validateMessages={validateMessages} size="small">
              <Form.Item label="Subject" className={!isSubjectValid ? 'subject-form-item-errored' : ''}>
                <Spin spinning={editorLoading}>
                  <Editor
                    id={mceEditorId}
                    onInit={(evt, editor) => {
                      editor.focus();
                      setEditorLoading(false);
                    }}
                    onChange={(e) => handleSubjectChange(e)}
                    init={{
                      height: 150,
                      ...getTinyMCEEditorTheme(),
                      menubar: false,
                      statusbar: false,
                      forced_root_block: false,
                      browser_spellcheck: true,
                      plugins: 'lists advlist code',
                      toolbar_mode: 'sliding',
                      toolbar:
                        'undo redo | fontselect fontsizeselect | bold italic underline strikethrough | forecolor backcolor |\
                         alignleft aligncenter alignright |\
                         outdent indent | bullist numlist | code',
                    }}
                  />
                  {!isSubjectValid ? <p style={{ color: 'red', marginTop: '3px', marginBottom: 0 }}>This field is required!</p> : null}
                </Spin>
              </Form.Item>
            </Form>

            {!isSuperUserNotes ? (
              <ReminderComponent
                form={reminderForm}
                isReminderApplied={isReminderApplied}
                changeReminder={handleReminderChange}
              />
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                ghost
                htmlType="button"
                size="small"
                style={{ marginRight: '10px' }}
                onClick={handleSave}
                loading={createNoteCommentLoading}
              >
                Submit
              </Button>

              <Button htmlType="button" type="primary" size="small" ghost onClick={() => cancelAddComment()}>
                Cancel
              </Button>
            </div>
          </div>
        </List.Item>
      );
    } else {
      return (
        <>
          <Badge.Ribbon text="Archived" placement="start" color="red" style={{ top: '-11px', display: !item.hide ? 'none' : '' }}>
            <List.Item>
              <Col flex="auto">
                <div>
                  <span className="note-owner">{item.lastChangeUserDisplayName}</span>
                  <span className="note-timestamp" title="Time of creation">
                    {' '}
                    <ClockCircleOutlined /> {dayjs(item?.createDate).format('MMM DD, YYYY hh:mm:ss A')}{' '}
                  </span>
                  {item.reminderDateTime ? (
                    <>
                      <span className="note-reminder" title="Reminder">
                        {' '}
                        <BellOutlined /> {dayjs(item.reminderDateTime).format('MMM DD, YYYY, hh:mm:ss A')}
                      </span>
                      <Popover
                        className="note-reminder-popover"
                        title={
                          <Space direction="horizontal">
                            <Text strong>Reminder date:</Text>
                            <Text>{dayjs(item.reminderDateTime).format('MMM DD, YYYY, hh:mm:ss A')}</Text>
                          </Space>
                        }
                        content={
                          <Space direction="horizontal">
                            <Text strong>Sent to emails:</Text>
                            <Space direction="vertical">
                              {remindersInfo &&
                                remindersInfo.map((email, index) => (
                                  <Text className="note-reminder-popover-email" key={index}>
                                    {email}
                                  </Text>
                                ))}
                            </Space>
                          </Space>
                        }
                        trigger="hover"
                        placement="right"
                      >
                        <ExclamationCircleOutlined />
                      </Popover>
                    </>
                  ) : null}
                </div>

                <p className="comment-message" ref={messageRef}>
                  <IFrame
                    frameBorder="0"
                    styleSelector='link[href*="React_Sources"], link[href*="react-sources-cdn"]'
                    scrolling="yes"
                    className="iframe-subject"
                  >
                    <div dangerouslySetInnerHTML={{ __html: item.subject }} />
                  </IFrame>
                </p>
              </Col>
              <Col flex="80px">
                {item.hide ? (
                  <Button
                    style={{ marginRight: '10px' }}
                    type="primary"
                    ghost
                    icon={<RollbackOutlined />}
                    size="small"
                    onClick={() => handleArchiveComment(item.id, false)}
                  >
                    Unarchive
                  </Button>
                ) : (
                  <Dropdown menu={{ items: menu }} trigger={['click']}>
                    <Button type="primary" ghost size="small" style={{ fontSize: '12px' }}>
                      Actions <DownOutlined />
                    </Button>
                  </Dropdown>
                )}
              </Col>
            </List.Item>
          </Badge.Ribbon>

          {reassignModalVisible ? (
            <ReAssignModal
              visible={reassignModalVisible}
              close={(shouldRefresh) => reassignModalClose(shouldRefresh)}
              noteId={item.id}
            />
          ) : null}

          {assignNoteToTaskModalVisible ? (
            <AssignNoteToTaskModal
              visible={assignNoteToTaskModalVisible}
              insuredId={insuredId}
              note={item}
              close={() => setAssignNoteToTaskModalVisible(false)}
              refresh={() => assignNoteToTaskModalClose()}
            />
          ) : null}
        </>
      );
    }
  }

  return <></>;
};

export default Comment;
